import { CurrencyPipe } from '@angular/common';
import { ElementRef } from '@angular/core';
import { formatTypes } from '@zipari/shared-ds-util-format';
import * as d3 from 'd3';
import { InitializeBarChartService } from '../bar-chart-init.service';
import { getChartWidth } from '../horizontal-bar-graph.helper';
import { BarGraphData } from './bar-graph.model';

export class BarChartService extends InitializeBarChartService {
  barGraphConfig;
  data: BarGraphData[];
  xScale: d3.ScaleLinear<number, number, never>;
  yScale: d3.ScaleBand<string>;
  yScale0: d3.ScaleBand<string>;
  yScale1: d3.ScaleBand<string>;
  groupedData: BarGraphData[][];
  currencyPipe: CurrencyPipe;

  constructor(
    element: ElementRef,
    config,
    data: BarGraphData[],
    currencyPipe?: CurrencyPipe,
  ) {
    super(element, config);
    this.element = element;
    this.data = data;
    this.barGraphConfig = config;
    this.currencyPipe = currencyPipe;
  }

  createBarGraph(): void {
    this.groupedData = this.groupBy(this.data);
    this.createSvg();
    this.setScales();
    if (this.groupedData.length > 1) {
      this.createAxes(this.xScale, this.yScale0);
    } else {
      this.createAxes(this.xScale, this.yScale);
    }
    this.createGridLines(this.xScale);
    this.createGraphBars();
    if (this.barGraphConfig.showLegends) {
      super.createLegends(this.categories);
    }
  }

  setScales(): void {
    if (this.groupedData.length > 1) {
      this.yScale0 = this.getYAxis(this.barGraphConfig.yGroupPadding).domain(
        this.data.map((d) => d.group),
      );
      this.yScale1 = this.getGroupYAxis(
        this.barGraphConfig.ySubGroupPadding,
        this.yScale0.bandwidth(),
      ).domain(this.categories);
      this.xScale = this.getXAxis().domain([
        0,
        d3.max(this.groupedData, (_, i) =>
          d3.max([...this.groupedData[i]], (key) => {
            const chartWidth = getChartWidth(key, this.barGraphConfig);

            return chartWidth;
          }),
        ),
      ]);
    } else {
      this.xScale = this.getXAxis().domain([
        0,
        d3.max(this.data, (barGraphData: BarGraphData) => {
          const chartWidth = getChartWidth(barGraphData, this.barGraphConfig);

          return chartWidth;
        }),
      ]);

      this.yScale = this.getYAxis(this.barGraphConfig.yAxisPadding).domain(
        this.data.map((barGraphData: BarGraphData) => barGraphData.category),
      );
    }
  }

  groupBy(array: BarGraphData[]): BarGraphData[][] {
    const groupedObject = array.reduce((result, currentValue) => {
      (result[currentValue.group] = result[currentValue.group] || []).push(
        currentValue,
      );

      return result;
    }, {});

    return Object.keys(groupedObject).map((value) => groupedObject[value]);
  }

  get categories(): string[] {
    return [...new Set(this.data.map((item) => item.category))];
  }

  createGraphBars(): void {
    const colorScale = d3
      .scaleOrdinal()
      .domain(this.categories)
      .range(this.barGraphConfig.colorScheme);

    if (this.groupedData.length > 1) {
      const group = this.svg
        .selectAll('.Group')
        .data(this.groupedData)
        .enter()
        .append('g')
        .attr('class', 'Group')
        .attr(
          'transform',
          (d, i) =>
            `translate(0,${this.yScale0(d[i] ? d[i].group : d[0].group)})`,
        );

      group
        .selectAll('rect')
        .data((d) => d)
        .enter()
        .append('rect')
        .style('fill', (d) => colorScale(d.category))
        .attr('x', (_) => this.xScale(0))
        .attr('y', (d) => this.yScale1(d.category))
        .attr('width', (d) => this.xScale(d.value) - this.xScale(0))
        .attr('height', this.yScale1.bandwidth());

      group
        .selectAll('.text')
        .data((d) => d)
        .enter()
        .append('text')
        .attr('class', 'Subgroup')
        .attr(
          'y',
          (d) => this.yScale1(d.category) + this.barGraphConfig.labelTopMargin,
        )
        .attr(
          'x',
          -(this.barGraphConfig.tickPadding
            ? this.barGraphConfig.tickPadding
            : this.barGraphConfig.tickPadding),
        )
        .text((d) => d.category);

      group
        .selectAll('.text')
        .data((d) => d)
        .enter()
        .append('text')
        .attr('class', 'Label')
        .attr(
          'y',
          (d) => this.yScale1(d.category) + this.barGraphConfig.labelTopMargin,
        )
        .attr(
          'x',
          (d) => this.xScale(d.value) + this.barGraphConfig.labelLeftMargin,
        )
        .text(
          (d) =>
            `${
              this.barGraphConfig.formatType === formatTypes.CURRENCY
                ? this.currencyPipe.transform(d.value)
                : d.value
            }`,
        );
    } else {
      const data: BarGraphData[] = this.data;

      this.svg
        .selectAll('.bar')
        .data(data)
        .enter()
        .append('rect')
        .attr('class', 'bar')
        .attr('x', 0)
        .attr('y', (d) => this.yScale(d.category))
        .attr('width', (d) => this.xScale(d.value))
        .attr('fill', (d) => colorScale(d.category))
        .attr('height', this.yScale.bandwidth());

      this.svg
        .selectAll('.text')
        .data(data)
        .enter()
        .append('text')
        .attr('class', 'Label')
        .attr(
          'y',
          (d) => this.yScale(d.category) + this.barGraphConfig.labelTopMargin,
        )
        .attr(
          'x',
          (d) => this.xScale(d.value) + this.barGraphConfig.labelLeftMargin,
        )
        .text(
          (d) =>
            `${
              this.barGraphConfig.formatType === formatTypes.CURRENCY
                ? this.currencyPipe.transform(d.value)
                : d.value
            }`,
        );
    }
  }
}
